import { Component, inject, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogTitle, MatDialogClose } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer'
import { TranslatePipe } from '@ngx-translate/core'
import { firstValueFrom } from 'rxjs'

export type PdfPreviewDialogData = {
  downloadUrl: string
  fileName: string
}

@Component({
  selector: 'app-pdf-preview-dialog',
  standalone: true,
  imports: [
    MatDialogContent,
    MatDialogTitle,
    MatDialogClose,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    NgxExtendedPdfViewerModule,
    TranslatePipe,
  ],
  templateUrl: './pdf-preview-dialog.component.html',
  styleUrls: ['./pdf-preview-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfPreviewDialogComponent implements OnInit {
  data: PdfPreviewDialogData = inject(MAT_DIALOG_DATA)
  dialogRef = inject(MatDialogRef<PdfPreviewDialogComponent>)
  http = inject(HttpClient)
  changeDetectorRef = inject(ChangeDetectorRef)

  pdfBlob: Blob | null = null
  isLoading = true
  hasError = false
  zoomLevel: number | null = null

  // Fixed percentage steps, the same shape pdf.js's own zoom presets use. Stepping through a
  // known-good list avoids deriving the next zoom from the viewer's live reported scale, which
  // corrupted the canvas when fed back into [zoom].
  private static readonly ZOOM_PRESETS = [50, 75, 100, 125, 150, 175, 200, 250, 300, 400]
  private zoomIndex = 2 // 100%, the entry point once the user leaves page-fit

  ngOnInit() {
    this.loadPdf()
  }

  zoomIn() {
    this.zoomIndex = Math.min(this.zoomIndex + 1, PdfPreviewDialogComponent.ZOOM_PRESETS.length - 1)
    this.zoomLevel = PdfPreviewDialogComponent.ZOOM_PRESETS[this.zoomIndex]
  }

  zoomOut() {
    this.zoomIndex = Math.max(this.zoomIndex - 1, 0)
    this.zoomLevel = PdfPreviewDialogComponent.ZOOM_PRESETS[this.zoomIndex]
  }

  private async loadPdf(): Promise<void> {
    if (this.data.downloadUrl === null || this.data.downloadUrl === undefined) {
      console.error('PDF download URL is not available')
      this.hasError = true
      this.isLoading = false
      this.changeDetectorRef.markForCheck()
      return
    }
    try {
      this.pdfBlob = await firstValueFrom(this.http.get(this.data.downloadUrl, { responseType: 'blob' }))
    } catch (error) {
      console.error('Failed to load PDF:', error)
      this.hasError = true
    } finally {
      this.isLoading = false
      this.changeDetectorRef.markForCheck()
    }
  }

  close() {
    this.dialogRef.close()
  }
}
